import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { ConditionalSkipService } from '@/services/conditional-skip-service';

interface BulkDeleteType1DialogProps {
  totalCount: number;
}

export const BulkDeleteType1Dialog: React.FC<BulkDeleteType1DialogProps> = ({ totalCount }) => {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: () => ConditionalSkipService.bulkDeleteType1Settings(),
    onSuccess: (result) => {
      if (result.error) {
        toast.error(`Failed to delete: ${result.error}`);
      } else {
        toast.success(`Successfully deleted ${result.deletedCount} Type 1 bot settings`);
        queryClient.invalidateQueries({ queryKey: ['conditional-skip-states-monitor'] });
        queryClient.invalidateQueries({ queryKey: ['conditional-skip-win-rates'] });
        setOpen(false);
      }
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.message}`);
    }
  });

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button variant="destructive" size="sm">
          <Trash2 className="h-4 w-4 mr-2" />
          Delete All Type 1 ({totalCount})
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete All Type 1 Bots?</AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <p>
              This will permanently delete <strong>{totalCount} Type 1</strong> (START_STOP_AFTER_LOSS) conditional skip settings.
            </p>
            <p className="text-destructive font-medium">
              ⚠️ This action cannot be undone!
            </p>
            <p className="text-sm">
              The following will be deleted:
            </p>
            <ul className="text-sm list-disc list-inside space-y-1">
              <li>All Type 1 settings from conditional_skip_settings</li>
              <li>Current state data from bot_current_condition_skip</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-2">
              Note: The bots themselves will remain active but without conditional skip logic.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              deleteMutation.mutate();
            }}
            disabled={deleteMutation.isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {deleteMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Deleting...
              </>
            ) : (
              'Delete All Type 1'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
